/**
 * ETag utilities for optimistic concurrency control.
 *
 * ETags are server-provided opaque tokens for concurrency control.
 * The frontend receives them in API responses and sends them back
 * with update requests via the If-Match header.
 *
 * This module provides a simple helper to build the If-Match header.
 * ETags are stored in React Query cache as part of the response objects -
 * there is no separate ETag store.
 */

/**
 * Build an If-Match header object from an ETag value.
 * Returns an empty object if no ETag is provided.
 *
 * @param etag - The ETag value from a previous response (or undefined)
 * @returns Header object to spread into fetch headers
 *
 * @example
 * ```ts
 * const response = await fetch(url, {
 *   headers: {
 *     ...getAuthHeaders(),
 *     ...buildIfMatchHeader(entity.etag),
 *   },
 * });
 * ```
 */
export function buildIfMatchHeader(etag: string | undefined): Record<string, string> {
  if (etag) {
    return { 'If-Match': etag };
  }
  return {};
}
